import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { styled } from "@glitz/react";
import { useCart } from "../context/CartContext";
import { useViewport } from "../hooks/useViewport";
import Base from "../components/basic/Base";
import Button from "../components/basic/Button";
import Select from "../components/basic/Select";
import Loader from "../components/basic/Loader";
import FreeShippingProgress from "../components/FreeShippingBar";
import { Theme, Size } from "../theme";
import {
  spacing,
  fontSize,
  fontWeight,
  green,
  darkGreen,
  orange,
  red,
  grey,
  darkGrey,
  lightGrey,
  white,
  black,
  yellow,
} from "../theme";

const API_BASE = process.env.REACT_APP_API_URL || "";

/* ─── types ─────────────────────────────────────────────────── */

interface SlimProduct {
  id: number;
  /** Core/shop SKU — används för paket-defaults */
  sku?: string;
  name: string;
  slug: string;
  price: string;
  image: string | null;
  inStock: boolean;
  shippingClass: string;
  /** Från WooCommerce meta carbs_per_serving */
  carbs?: number | null;
  /** Större förpackningar: portioner per enhet (valfritt) */
  servings_per_package?: number | null;
  /** Override: totala g kh per förpackning (valfritt) */
  carbs_per_package?: number | null;
  /** contains / none / unknown från Woo (energikalkylator m.m.) */
  caffeine?: "contains" | "none" | "unknown";
  /** Från WooCommerce meta package_price_adjustment (kr, +/- mot paketgrundpris) */
  package_price_adjustment?: number;
}

interface PackageItem {
  key: string;
  label: string;
  quantity: number;
  selectable: boolean;
  /** "main" (standard) eller "preRace" — preRace visas under egen rubrik */
  section?: "main" | "preRace";
  /** Om true: ingen kh i energiöversikt (t.ex. flaska), oberoende av WooCommerce-meta */
  excludeFromCarbs?: boolean;
  /** energy_pool-värden som fyller slotten (server) */
  energyPools?: string[];
  /** Förvald produkt (SKU) för alla enheter i slotten */
  defaultProductSku?: string;
  /** En förvald SKU per plats; längd bör matcha quantity; går före defaultProductSku */
  defaultSelectedSkus?: string[];
  /** @deprecated Woo-ID — behålls som fallback */
  defaultProductId?: number;
  /** @deprecated Woo-ID — behålls som fallback */
  defaultSelectedIds?: number[];
  defaultCarbs: number;
  carbsMap: Record<number, number>;
  hint: string;
  products: SlimProduct[];
}

interface EnergyPlanPhase {
  heading: string;
  summary: string;
  bullets: string[];
}

interface Package {
  slug: string;
  name: string;
  tagline: string;
  description: string;
  totalTimeHours: number;
  /** Grundpris; slutpris = packagePrice + sum(package_price_adjustment) per vald enhet */
  packagePrice?: number;
  /** Rekommenderad energiplan från paketdefinitionen */
  energyPlan?: {
    title: string;
    phases: EnergyPlanPhase[];
  };
  items: PackageItem[];
}

type Selections = Record<string, number[]>;

/* ─── helpers ───────────────────────────────────────────────── */

function getCarbsForProduct(item: PackageItem, productId: number): number {
  if (item.excludeFromCarbs) return 0;
  const p = item.products.find((pr) => pr.id === productId);
  if (p != null && typeof p.carbs === "number" && Number.isFinite(p.carbs)) {
    return p.carbs;
  }
  return item.carbsMap[productId] ?? item.defaultCarbs;
}

function formatAdjKr(n: number): string {
  if (n === 0) return "";
  const sign = n > 0 ? "+" : "";
  return ` (${sign}${Math.round(n)} kr)`;
}

/** Visningsvärde för g kolhydrater (heltal eller en decimal) */
function formatCarbsGrams(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

/** Matcha default-ref (SKU eller legacy Woo-ID) mot en produkt i slotten. */
function resolveProductIdByRef(
  item: PackageItem,
  ref: string | number | null | undefined
): number | null {
  if (ref == null || ref === "") return null;
  const raw = String(ref).trim();
  if (!raw) return null;
  const bySku = item.products.find(
    (p) => p.sku && p.sku.toLowerCase() === raw.toLowerCase()
  );
  if (bySku) return bySku.id;
  const asNum = Number(raw);
  if (Number.isFinite(asNum) && item.products.some((p) => p.id === asNum)) {
    return asNum;
  }
  return null;
}

/** Förvalda produkt-ID per plats utifrån packages.js (SKU) + faktiska produkter i slotten */
function resolveDefaultSelections(item: PackageItem): number[] {
  const ids = item.products.map((p) => p.id);
  if (ids.length === 0) return [];
  const fallback = (i: number) => ids[i % ids.length];

  return Array.from({ length: item.quantity }, (_, i) => {
    const skuRef = item.defaultSelectedSkus?.[i];
    const fromSku = resolveProductIdByRef(item, skuRef);
    if (fromSku != null) return fromSku;

    const legacyId = item.defaultSelectedIds?.[i];
    const fromLegacy = resolveProductIdByRef(item, legacyId);
    if (fromLegacy != null) return fromLegacy;

    const singleSku = resolveProductIdByRef(item, item.defaultProductSku);
    if (singleSku != null) return singleSku;

    const singleLegacy = resolveProductIdByRef(item, item.defaultProductId);
    if (singleLegacy != null) return singleLegacy;

    return fallback(i);
  });
}

function getCarbLevel(carbsPerHour: number) {
  if (carbsPerHour < 40)
    return { color: yellow, label: "Lågt", message: "Lågt — risk att gå tom på energi" };
  if (carbsPerHour <= 60)
    return { color: green, label: "Bra", message: "Bra grundnivå för de flesta" };
  if (carbsPerHour <= 90)
    return { color: darkGreen, label: "Optimalt", message: "Optimal nivå för de flesta" };
  if (carbsPerHour <= 120)
    return { color: orange, label: "Högt", message: "Högt — kräver tillvänjning" };
  return { color: red, label: "Mycket högt", message: "Mycket högt — stor risk för magproblem" };
}

function formatPrice(val: number) {
  return `${Math.round(val)} kr`;
}

/* ─── component ─────────────────────────────────────────────── */

const PackagePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { addToCart, setPackageFeeTotal } = useCart();
  const { isMobile } = useViewport();

  const [pkg, setPkg] = useState<Package | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selections, setSelections] = useState<Selections>({});
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/api/packages/${slug}`)
      .then((r) => {
        if (!r.ok) throw new Error("Paketet kunde inte hittas");
        return r.json();
      })
      .then((data: Package) => {
        setPkg(data);
        const initial: Selections = {};
        data.items.forEach((item) => {
          if (item.products.length > 0) {
            initial[item.key] = resolveDefaultSelections(item);
          }
        });
        setSelections(initial);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [slug]);

  /* ── energy calculations ── */

  const { totalCarbs, carbsPerHour, listSum, finalPrice, packageFeeTotal } = useMemo(() => {
    if (!pkg)
      return {
        totalCarbs: 0,
        carbsPerHour: 0,
        listSum: 0,
        finalPrice: 0,
        packageFeeTotal: 0,
      };

    let carbs = 0;
    let list = 0;
    let adjustmentSum = 0;

    pkg.items.forEach((item) => {
      const sel = selections[item.key] || [];
      sel.forEach((productId) => {
        carbs += getCarbsForProduct(item, productId);
        const p = item.products.find((pr) => pr.id === productId);
        if (p) {
          list += parseFloat(p.price) || 0;
          adjustmentSum += p.package_price_adjustment ?? 0;
        }
      });
    });

    const hasPackagePrice =
      pkg.packagePrice != null && Number.isFinite(pkg.packagePrice);
    const final = hasPackagePrice
      ? pkg.packagePrice! + adjustmentSum
      : list;

    return {
      totalCarbs: carbs,
      carbsPerHour: pkg.totalTimeHours > 0 ? Math.round(carbs / pkg.totalTimeHours) : 0,
      listSum: list,
      finalPrice: final,
      packageFeeTotal: final - list,
    };
  }, [pkg, selections]);

  const level = getCarbLevel(carbsPerHour);
  const barPercent = Math.min(carbsPerHour / 130, 1) * 100;

  const { mainItems, preRaceItems } = useMemo(() => {
    if (!pkg) return { mainItems: [] as PackageItem[], preRaceItems: [] as PackageItem[] };
    const main: PackageItem[] = [];
    const pre: PackageItem[] = [];
    pkg.items.forEach((item) => {
      if (item.section === "preRace") pre.push(item);
      else main.push(item);
    });
    return { mainItems: main, preRaceItems: pre };
  }, [pkg]);

  const preRaceAllExcludedFromCarbs =
    preRaceItems.length > 0 &&
    preRaceItems.every((item) => item.excludeFromCarbs === true);

  /* ── event handlers ── */

  const handleSelect = (itemKey: string, unitIndex: number, productId: string) => {
    setSelections((prev) => {
      const arr = [...(prev[itemKey] || [])];
      arr[unitIndex] = Number(productId);
      return { ...prev, [itemKey]: arr };
    });
    setAdded(false);
  };

  const handleAddToCart = () => {
    if (!pkg) return;

    pkg.items.forEach((item) => {
      const sel = selections[item.key] || [];
      const counts = new Map<number | string, number>();
      sel.forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));

      counts.forEach((qty, productId) => {
        const product = item.products.find((p) => p.id === productId);
        if (!product) return;
        addToCart(
          {
            id: (product as { coreId?: number | string }).coreId || product.id,
            sku: (product as { sku?: string }).sku || undefined,
            name: product.name,
            price: parseFloat(product.price) || 0,
            quantity: qty,
            img: product.image || undefined,
            in_stock: product.inStock,
            stock_status: product.inStock ? "instock" : "outofstock",
            shipping_class: product.shippingClass,
          },
          { isPackageBatch: true }
        );
      });
    });

    setPackageFeeTotal(packageFeeTotal);
    setAdded(true);
    setTimeout(() => setAdded(false), 3000);
  };

  /* ── render ── */

  if (loading) return <Loader ariaLabel="Laddar paket" />;
  if (error || !pkg) return <ErrorMsg>{error || "Något gick fel"}</ErrorMsg>;

  const selectOptions = (item: PackageItem) =>
    item.products.map((p) => {
      const carbs = getCarbsForProduct(item, p.id);
      const carbExtra = item.excludeFromCarbs
        ? ""
        : ` · ${formatCarbsGrams(carbs)} g kolhydrater`;
      const adj = p.package_price_adjustment ?? 0;
      const priceExtra = pkg?.packagePrice != null ? formatAdjKr(adj) : "";
      return { value: String(p.id), label: `${p.name}${priceExtra}${carbExtra}` };
    });

  const renderSlotList = (items: PackageItem[]) =>
    items.map((item) => (
      <SlotSection key={item.key}>
        <SlotHeader>
          <SlotLabel>
            {item.quantity} &times; {item.label}
          </SlotLabel>
          {item.excludeFromCarbs ? (
            <SlotCarbs>ingår ej i kh</SlotCarbs>
          ) : (
            item.defaultCarbs > 0 && <SlotCarbs>{item.defaultCarbs} g kh/st</SlotCarbs>
          )}
        </SlotHeader>
        {item.hint && <SlotHint>{item.hint}</SlotHint>}

        {item.products.length === 0 ? (
          <SlotHint>Inga produkter konfigurerade ännu.</SlotHint>
        ) : (
          <UnitsGrid>
            {Array.from({ length: item.quantity }).map((_, i) => {
              const selectedId = selections[item.key]?.[i];
              const selectedProduct = item.products.find((p) => p.id === selectedId);

              return (
                <UnitCard key={i}>
                  {selectedProduct?.image && (
                    <ProductThumb src={selectedProduct.image} alt="" />
                  )}
                  {item.selectable ? (
                    <Select
                      label=""
                      options={selectOptions(item)}
                      value={String(selectedId ?? "")}
                      onChange={(val) => handleSelect(item.key, i, val)}
                    />
                  ) : (
                    <FixedName>{selectedProduct?.name || item.products[0]?.name}</FixedName>
                  )}
                </UnitCard>
              );
            })}
          </UnitsGrid>
        )}
      </SlotSection>
    ));

  return (
    <PageWrapper>
      <PageInner className={isMobile ? "package-page-mobile" : "package-page-desktop"}>
        {/* ── left column: items ── */}
        <MainColumn className="package-main">
          <PackageName>{pkg.name}</PackageName>
          <Tagline>{pkg.tagline}</Tagline>
          <Description>{pkg.description}</Description>

          {preRaceItems.length > 0 && (
            <>
              <PreRaceSectionHeading>Pre-race &amp; raceload</PreRaceSectionHeading>
              <PreRaceIntro>
                {preRaceAllExcludedFromCarbs
                  ? "Val här ingår i paketpriset men räknas inte in i kolhydrats- och energiöversikten (t.ex. flaska eller tillbehör till laddning)."
                  : "Produkter inför start som ingår i paketpriset. Kolhydrater räknas med i energiöversikten när produkten har kh i sortimentet — följ hinten."}
              </PreRaceIntro>
              {renderSlotList(preRaceItems)}
            </>
          )}

          {pkg.energyPlan && pkg.energyPlan.phases.length > 0 && (
            <EnergyPlanBlock>
              <EnergyPlanHeading>Energiplan</EnergyPlanHeading>
              <EnergyPlanTitle>{pkg.energyPlan.title}</EnergyPlanTitle>
              {pkg.energyPlan.phases.map((phase, i) => (
                <EnergyPhase key={i}>
                  <EnergyPhaseHeading>{phase.heading}</EnergyPhaseHeading>
                  <EnergyPhaseSummary>{phase.summary}</EnergyPhaseSummary>
                  {phase.bullets.length > 0 && (
                    <EnergyBulletList>
                      {phase.bullets.map((line, j) => (
                        <EnergyBulletItem key={j}>{line}</EnergyBulletItem>
                      ))}
                    </EnergyBulletList>
                  )}
                </EnergyPhase>
              ))}
            </EnergyPlanBlock>
          )}

          <SlotsTitle>Bygg ditt paket</SlotsTitle>
          {renderSlotList(mainItems)}
        </MainColumn>

        {/* ── right column: dashboard + cart ── */}
        <SideColumn className="package-side">
          <Dashboard>
            <DashboardTitle>Energiöversikt</DashboardTitle>
            <DashboardRow>
              <DashboardLabel>Kolhydrater / timme</DashboardLabel>
              <DashboardValue css={{ color: level.color }}>
                {carbsPerHour} g/h
              </DashboardValue>
            </DashboardRow>
            <BarOuter>
              <BarFill css={{ width: `${barPercent}%`, backgroundColor: level.color }} />
            </BarOuter>
            <BarLegend>
              <BarTick css={{ left: `${(40 / 130) * 100}%` }}>40</BarTick>
              <BarTick css={{ left: `${(60 / 130) * 100}%` }}>60</BarTick>
              <BarTick css={{ left: `${(90 / 130) * 100}%` }}>90</BarTick>
              <BarTick css={{ left: `${(120 / 130) * 100}%` }}>120</BarTick>
            </BarLegend>
            <LevelMessage css={{ color: level.color }}>{level.message}</LevelMessage>
            <DashboardRow>
              <DashboardLabel>Totalt i paketet</DashboardLabel>
              <DashboardValue>{totalCarbs} g kolhydrater</DashboardValue>
            </DashboardRow>
            <DashboardRow>
              <DashboardLabel>Total tid</DashboardLabel>
              <DashboardValue>{pkg.totalTimeHours} h</DashboardValue>
            </DashboardRow>
          </Dashboard>

          <PriceRow>
            <PriceCol>
              {pkg.packagePrice != null && packageFeeTotal !== 0 && (
                <ListPriceLine>Ord. pris {formatPrice(listSum)}</ListPriceLine>
              )}
              <PriceLabel>Att betala</PriceLabel>
            </PriceCol>
            <PriceTag>{formatPrice(finalPrice)}</PriceTag>
          </PriceRow>
          {pkg.packagePrice != null && (
            <PriceHint>
              Paketpris {formatPrice(pkg.packagePrice)} + valda tillägg/avdrag på produkterna.
            </PriceHint>
          )}

          <Button
            as={added ? Theme.Positive : Theme.Primary}
            size={Size.Large}
            fullWidth
            onClick={handleAddToCart}
          >
            {added ? "Tillagt i varukorgen!" : "Lägg paketet i varukorgen"}
          </Button>

          <FreeShippingProgress />
        </SideColumn>
      </PageInner>
    </PageWrapper>
  );
};

export default PackagePage;

/* ─── styled components ─────────────────────────────────────── */

const PageWrapper = styled(Base, {
  padding: { xy: spacing.xxxl },
});

const PageInner = styled.div({
  display: "flex",
  flexDirection: "row",
  gap: spacing.xl,
});

const MainColumn = styled.div({
  width: "60%",
});

const SideColumn = styled.div({
  width: "40%",
  position: "sticky",
  top: "120px",
  alignSelf: "flex-start",
  display: "flex",
  flexDirection: "column",
  gap: spacing.l,
});

const PackageName = styled.h1({
  fontSize: fontSize.xxl,
  fontWeight: fontWeight.heavy,
  margin: { bottom: spacing.xs },
});

const Tagline = styled.p({
  fontSize: fontSize.ml,
  color: darkGrey,
  margin: { bottom: spacing.s },
});

const Description = styled.p({
  fontSize: fontSize.m,
  lineHeight: "1.6",
  color: black,
  margin: { bottom: spacing.xl },
});

const EnergyPlanBlock = styled.section({
  backgroundColor: lightGrey,
  borderRadius: "8px",
  padding: { xy: spacing.l },
  margin: { bottom: spacing.xl },
});

const EnergyPlanHeading = styled.h2({
  fontSize: fontSize.xl,
  fontWeight: fontWeight.bold,
  margin: { bottom: spacing.s },
});

const EnergyPlanTitle = styled.p({
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
  color: darkGrey,
  margin: { bottom: spacing.m },
});

const EnergyPhase = styled.div({
  marginBottom: spacing.m,
  ":last-child": { marginBottom: 0 },
});

const EnergyPhaseHeading = styled.h3({
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
  margin: { bottom: spacing.xxs },
  color: black,
});

const EnergyPhaseSummary = styled.p({
  fontSize: fontSize.s,
  lineHeight: "1.55",
  color: black,
  margin: { bottom: spacing.xs },
});

const EnergyBulletList = styled.ul({
  fontSize: fontSize.s,
  lineHeight: "1.55",
  color: black,
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
  paddingLeft: spacing.l,
  listStyleType: "disc",
});

const EnergyBulletItem = styled.li({
  marginBottom: spacing.xxs,
  ":last-child": { marginBottom: 0 },
});

const ErrorMsg = styled.p({
  textAlign: "center",
  padding: { xy: spacing.xxxxl },
  color: red,
  fontSize: fontSize.l,
});

/* ── slots ── */

const SlotsTitle = styled.h2({
  fontSize: fontSize.xl,
  fontWeight: fontWeight.bold,
  margin: { bottom: spacing.l },
});

const PreRaceSectionHeading = styled.h2({
  fontSize: fontSize.xl,
  fontWeight: fontWeight.bold,
  margin: { top: 0, bottom: spacing.s },
});

const PreRaceIntro = styled.p({
  fontSize: fontSize.s,
  color: darkGrey,
  lineHeight: "1.5",
  margin: { bottom: spacing.l },
});

const SlotSection = styled.section({
  marginBottom: spacing.xl,
  paddingBottom: spacing.l,
  borderBottom: { style: "solid", color: grey, width: "1px" },
});

const SlotHeader = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  marginBottom: spacing.xxs,
});

const SlotLabel = styled.h3({
  fontSize: fontSize.ml,
  fontWeight: fontWeight.bold,
  margin: { bottom: 0 },
});

const SlotCarbs = styled.span({
  fontSize: fontSize.xs,
  color: darkGrey,
});

const SlotHint = styled.p({
  fontSize: fontSize.s,
  color: darkGrey,
  marginBottom: spacing.s,
  fontStyle: "italic",
});

const UnitsGrid = styled.div({
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.m,
});

const UnitCard = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "160px",
  gap: spacing.xs,
});

const ProductThumb = styled.img({
  width: "120px",
  height: "120px",
  objectFit: "contain",
  borderRadius: "4px",
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  backgroundColor: white,
});

const FixedName = styled.span({
  fontSize: fontSize.s,
  textAlign: "center",
  color: black,
});

/* ── dashboard ── */

const Dashboard = styled.section({
  backgroundColor: lightGrey,
  borderRadius: "8px",
  padding: { xy: spacing.l },
});

const DashboardTitle = styled.h2({
  fontSize: fontSize.l,
  fontWeight: fontWeight.bold,
  margin: { bottom: spacing.m },
});

const DashboardRow = styled.div({
  display: "flex",
  justifyContent: "space-between",
  marginBottom: spacing.xs,
});

const DashboardLabel = styled.span({
  fontSize: fontSize.s,
  color: darkGrey,
});

const DashboardValue = styled.span({
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
});

const BarOuter = styled.div({
  height: "8px",
  backgroundColor: grey,
  borderRadius: "4px",
  overflow: "hidden",
  marginTop: spacing.xs,
});

const BarFill = styled.div({
  height: "100%",
  borderRadius: "4px",
  transitionProperty: "width, background-color",
  transitionDuration: "0.3s",
  transitionTimingFunction: "ease-in-out",
});

const BarLegend = styled.div({
  position: "relative",
  height: "20px",
  marginBottom: spacing.xs,
});

const BarTick = styled.span({
  position: "absolute",
  fontSize: fontSize.xxs,
  color: darkGrey,
  transform: "translateX(-50%)",
  top: "4px",
});

const LevelMessage = styled.p({
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  marginBottom: spacing.m,
});

/* ── price / cart ── */

const PriceRow = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
});

const PriceCol = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
});

const ListPriceLine = styled.span({
  fontSize: fontSize.s,
  color: darkGrey,
  textDecoration: "line-through",
});

const PriceLabel = styled.span({
  fontSize: fontSize.m,
  color: darkGrey,
});

const PriceTag = styled.span({
  fontSize: fontSize.xl,
  fontWeight: fontWeight.heavy,
});

const PriceHint = styled.p({
  fontSize: fontSize.xs,
  color: darkGrey,
  margin: { top: spacing.xs },
});
